import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';
import type { AnalyticsOverview, InstagramPost } from './types';
import { STATUS_STYLES, formatStatus } from './types';

interface OverviewComponentProps {
  setActiveTab: (tab: string) => void;
}

const OverviewComponent: React.FC<OverviewComponentProps> = ({ setActiveTab }) => {
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [recentPosts, setRecentPosts] = useState<InstagramPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const [overviewRes, postsRes] = await Promise.all([
        apiService.getInstagramAnalyticsOverview(),
        apiService.getInstagramPosts({ limit: 5 }),
      ]);

      const overviewData = overviewRes as unknown as { overview: AnalyticsOverview };
      const postsData = postsRes as unknown as { posts: InstagramPost[] };

      setOverview(overviewData.overview);
      setRecentPosts(postsData.posts || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load overview');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-blue" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-brand-red/30 shadow-sm">
        <p className="text-brand-red font-body">{error}</p>
        <button
          onClick={loadData}
          className="mt-4 bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-6 py-3 rounded-xl transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  const budget = overview?.budget;
  const budgetUsedPct = budget?.monthlyCap
    ? Math.min((budget.allocated / budget.monthlyCap) * 100, 100)
    : 0;

  return (
    <div className="space-y-8">
      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm hover:shadow-md transition-shadow">
          <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Pending Approval</h3>
          <div className="text-3xl font-bold text-brand-red">{overview?.pendingApproval ?? 0}</div>
          <p className="text-gray-600 text-sm">Awaiting your review</p>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm hover:shadow-md transition-shadow">
          <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Published</h3>
          <div className="text-3xl font-bold text-scarf-green">{overview?.publishedThisMonth ?? 0}</div>
          <p className="text-gray-600 text-sm">This month</p>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm hover:shadow-md transition-shadow">
          <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Scheduled</h3>
          <div className="text-3xl font-bold text-brand-blue">{overview?.scheduled ?? 0}</div>
          <p className="text-gray-600 text-sm">Queued to post</p>
        </div>
        <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm hover:shadow-md transition-shadow">
          <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Engagement Rate</h3>
          <div className="text-3xl font-bold text-brand-blue-light">{overview?.engagementRate ?? 0}%</div>
          <p className="text-gray-600 text-sm">Likes + comments / reach</p>
        </div>
      </div>

      {/* Budget */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-title font-bold text-deep-navy">Ad Budget</h3>
          <span className="text-sm font-body text-gray-600">
            {budget?.activeCampaigns ?? 0} active {budget?.activeCampaigns === 1 ? 'campaign' : 'campaigns'}
          </span>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mb-4">
          <div className="bg-frost-white rounded-xl p-4">
            <p className="text-sm text-gray-600 font-body">Monthly Cap</p>
            <p className="text-2xl font-bold text-deep-navy">${budget?.monthlyCap?.toFixed(2) ?? '0.00'}</p>
          </div>
          <div className="bg-frost-white rounded-xl p-4">
            <p className="text-sm text-gray-600 font-body">Allocated</p>
            <p className="text-2xl font-bold text-brand-blue">${budget?.allocated?.toFixed(2) ?? '0.00'}</p>
          </div>
          <div className="bg-frost-white rounded-xl p-4">
            <p className="text-sm text-gray-600 font-body">Spent</p>
            <p className="text-2xl font-bold text-brand-red">${budget?.spent?.toFixed(2) ?? '0.00'}</p>
          </div>
        </div>

        <div className="w-full bg-frost-white rounded-full h-3 overflow-hidden">
          <div
            className="bg-brand-blue h-full rounded-full transition-all duration-500"
            style={{ width: `${budgetUsedPct}%` }}
          />
        </div>
        <p className="text-sm text-gray-600 font-body mt-2">
          ${budget?.remaining?.toFixed(2) ?? '0.00'} remaining to allocate
        </p>
      </div>

      {/* Quick actions */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Quick Actions</h3>
        <div className="flex flex-wrap gap-4">
          <button
            onClick={() => setActiveTab('content-studio')}
            className="bg-brand-blue hover:bg-brand-blue-dark text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            Create Content
          </button>
          <button
            onClick={() => setActiveTab('campaigns')}
            className="bg-brand-blue-light hover:bg-brand-blue text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            Manage Campaigns
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className="bg-frost-white hover:bg-ice-teal/20 text-deep-navy font-bold px-6 py-3 rounded-xl transition-colors border border-ice-teal-light"
          >
            View Analytics
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className="bg-frost-white hover:bg-ice-teal/20 text-deep-navy font-bold px-6 py-3 rounded-xl transition-colors border border-ice-teal-light"
          >
            Connection Settings
          </button>
        </div>
      </div>

      {/* Recent posts */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Recent Posts</h3>

        {recentPosts.length === 0 ? (
          <p className="text-gray-600 font-body py-8 text-center">
            No posts yet. Head to Content Studio to create your first one.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ice-teal-light">
                  <th className="text-left py-3 px-4 text-deep-navy font-bold">Caption</th>
                  <th className="text-left py-3 px-4 text-deep-navy font-bold">Theme</th>
                  <th className="text-left py-3 px-4 text-deep-navy font-bold">Status</th>
                  <th className="text-left py-3 px-4 text-deep-navy font-bold">Created</th>
                </tr>
              </thead>
              <tbody>
                {recentPosts.map((post) => (
                  <tr key={post._id} className="border-b border-gray-100 hover:bg-frost-white transition-colors">
                    <td className="py-3 px-4 font-body text-deep-navy max-w-md truncate">
                      {post.caption}
                    </td>
                    <td className="py-3 px-4 font-body text-gray-600 text-sm">
                      {post.contentTheme || '—'}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${STATUS_STYLES[post.status] || 'bg-gray-100 text-gray-700'}`}>
                        {formatStatus(post.status)}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-body text-gray-600 text-sm">
                      {new Date(post.createdAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default OverviewComponent;
